import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemButton,
  ListItemIcon,
  ListSubheader,
  Select,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { asseticomRouteConfig, clientRouteConfig } from '../config/routes';

import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useTheme } from '@mui/styles';
import { Crisp } from 'crisp-sdk-web';
import {
  getFirebase,
  useFirestoreConnect,
  type ReduxFirestoreQuerySetting,
} from 'react-redux-firebase';
import { DRAWER_WIDTH } from '../config/constants';
import { AppState } from '../config/store';
import { isViewingClientPortal } from '../shared/utilities';
import { setNavOpen } from '../state/ui';
import AccessibleSwitcher from './AccessibleSwitcher';
import ComponentGuard from './ComponentGuard';
import DynamicIcon from './DynamicIcon';
import UserInfo from './UserInfo';

const SideNav = () => {
  const firebase = getFirebase();
  const [routeConfig, setRouteConfig] = useState(asseticomRouteConfig);
  const [routePrefix, setRoutePrefix] = useState('');
  const theme = useTheme();
  const bigScreen = useMediaQuery(theme.breakpoints.up('md'));
  const navType = bigScreen ? 'persistent' : 'temporary';
  const uiState = useSelector((state: AppState) => state.ui);
  const auth = useSelector((state: AppState) => state.firebase.auth);
  const rolesArray: ReduxFirestoreQuerySetting[] = useMemo(() => {
    if (auth.uid) {
      return [
        {
          collection: 'roles',
          where: [
            ['uid', '==', auth.uid],
            ['deleted', '==', 0],
          ],
          storeAs: 'userRoles',
        },
      ];
    } else {
      return [];
    }
  }, [auth.uid]);

  useFirestoreConnect(rolesArray);

  const userRoles = useSelector(
    (state: AppState) => state.firestore.ordered.userRoles,
  );

  const hasMoreThanOneRole = userRoles?.length > 1;

  const location = useLocation();

  const client = useSelector((state: AppState) => state.firestore.data.client);

  const currentClientId = client?.id;

  useEffect(() => {
    if (client && isViewingClientPortal(client.id)) {
      setRouteConfig(clientRouteConfig);
      setRoutePrefix(`/${client.id}`);
    } else {
      setRoutePrefix('');
      setRouteConfig(asseticomRouteConfig);
    }
  }, [location, client]);

  const dispatch = useDispatch();

  const closeNav = () => {
    dispatch(setNavOpen({ open: false }));
  };

  function onSelectPortal(clientId: string) {
    window.location.href = `/${clientId}`;
  }

  return (
    <Drawer
      sx={{
        width: DRAWER_WIDTH,
      }}
      elevation={20}
      open={bigScreen ? true : uiState.navOpen}
      variant={navType}
      onClose={closeNav}
    >
      <Toolbar color="secondary" />
      <Box
        sx={{
          width: DRAWER_WIDTH,
          overflow: 'auto',
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
        }}
      >
        {!bigScreen && (
          <Box display="flex" justifyContent="flex-end">
            <IconButton
              sx={{ transform: `rotateY(180deg)` }}
              onClick={closeNav}
              size="large"
            >
              <DynamicIcon name="arrow" colour="secondary" size={20} />
            </IconButton>
          </Box>
        )}
        <UserInfo />
        <Divider />

        {currentClientId && hasMoreThanOneRole ? (
          <Box>
            <List sx={{ paddingBottom: 0 }}>
              <ListItem sx={{ paddingX: 2, paddingBottom: 0 }}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  // error={!!errors.estimatedDuration}
                  className=".MuiFormControl-marginNormal"
                >
                  <InputLabel htmlFor="estimatedDuration">
                    Select Portal
                  </InputLabel>

                  <Select
                    variant="outlined"
                    label="Select Portal"
                    native
                    value={currentClientId}
                    // inputRef={field.ref}
                    // error={!!errors.estimatedDuration}
                    onChange={(event) => {
                      onSelectPortal(event.target.value);
                      // return field.onChange(+event.target.value);
                    }}
                  >
                    <option key={0} value="" aria-label="empty" />
                    {userRoles?.map((role) => (
                      <option key={role.clientId} value={role.clientId}>
                        {role.clientName ?? role.clientId}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </ListItem>
            </List>
          </Box>
        ) : null}
        <Box
          display="flex"
          justifyContent="space-between"
          flexDirection="column"
          flex={1}
        >
          <List sx={{ paddingTop: 0 }}>
            {routeConfig.map((config) => {
              const { subHeading, auth: parentAuth } = config;

              return (
                <ComponentGuard
                  auth={parentAuth}
                  key={subHeading}
                  label={subHeading}
                >
                  <ListSubheader disableSticky sx={{ my: 1 }}>
                    {subHeading}
                  </ListSubheader>

                  {config.items.map((item) => {
                    const {
                      text,
                      path,
                      icon,
                      auth: childAuth,
                      accessibleText,
                    } = item;

                    const content = (
                      <>
                        <ListItemIcon>
                          {icon ? (
                            <DynamicIcon name={icon} colour="secondary" />
                          ) : null}
                        </ListItemIcon>

                        <AccessibleSwitcher
                          accessibleChild={
                            <ListItemText>{accessibleText}</ListItemText>
                          }
                        >
                          <ListItemText aria-hidden="true">{text}</ListItemText>
                        </AccessibleSwitcher>
                      </>
                    );

                    return (
                      <ComponentGuard auth={childAuth} key={text} label={text}>
                        <ListItem sx={{ padding: 0 }}>
                          {path.startsWith('http') ? (
                            <ListItemButton dense href={path} target="_blank">
                              {content}
                            </ListItemButton>
                          ) : (
                            <ListItemButton
                              dense
                              key={path}
                              component={NavLink}
                              to={`${routePrefix}${path}`}
                              activeClassName="Mui-selected"
                              onClick={closeNav}
                              exact
                            >
                              {content}
                            </ListItemButton>
                          )}
                        </ListItem>
                      </ComponentGuard>
                    );
                  })}
                </ComponentGuard>
              );
            })}
          </List>
          <Box>
            <List>
              <ListSubheader disableSticky>Links</ListSubheader>

              <ListItem sx={{ padding: 0 }}>
                <ListItemButton
                  dense
                  href="https://asseticom.co.uk/legal/accessibility"
                  target="_blank"
                >
                  <ListItemText>Accessibility Statement</ListItemText>
                </ListItemButton>
              </ListItem>
              <ListItem sx={{ padding: 0 }}>
                <ListItemButton
                  dense
                  href="https://asseticom.co.uk/legal/privacy"
                  target="_blank"
                >
                  <ListItemText>Privacy Policy</ListItemText>
                </ListItemButton>
              </ListItem>
            </List>
            <List>
              <ListItem>
                <Button
                  color="secondary"
                  variant="contained"
                  fullWidth
                  onClick={async () => {
                    Crisp.setTokenId();
                    Crisp.session.reset();
                    await firebase.logout();
                  }}
                >
                  Log Out
                </Button>
              </ListItem>

              <ListItem dense>
                <Box my={2} flex={1}>
                  <Typography variant="caption">© Asseticom</Typography>
                </Box>
              </ListItem>
            </List>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default SideNav;
